import { ReactQueryStaleTime } from '@openx/constants/react-query';
import { QueryKeys } from '@openx/utils/api/queryKeys';
import { type InfiniteData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  SearchOptionsByPathDocument,
  type SearchOptionsByPathQuery,
  type SearchOptionsByPathQueryVariables,
} from '../graphql/query/options/SearchOptionsByPath.generated';
import { getGraphQLRequestFromClient } from '../modules/graphql/types';

type SearchOptionsByPathQueryOptions = {
  skip?: boolean;
  variables: SearchOptionsByPathQueryVariables;
};

type InfiniteSearchOptionsByPathQueryOptions = SearchOptionsByPathQueryOptions & {
  pageSize: number;
};

export const searchOptionsByPathQueryKey = (variables: SearchOptionsByPathQueryVariables) =>
  [QueryKeys.OPTIONS_LIST, 'SearchOptionsByPath', variables] as const;

export const infiniteSearchOptionsByPathQueryKey = (variables: SearchOptionsByPathQueryVariables, pageSize: number) =>
  [QueryKeys.OPTIONS_LIST, 'InfiniteSearchOptionsByPath', variables, pageSize] as const;

export const useSearchOptionsByPathQuery = ({ skip, variables }: SearchOptionsByPathQueryOptions) => {
  const result = useQuery({
    enabled: !skip,
    queryFn: ({ client }) => getGraphQLRequestFromClient(client)(SearchOptionsByPathDocument, variables),
    queryKey: searchOptionsByPathQueryKey(variables),
    staleTime: ReactQueryStaleTime.MINUTES_15,
  });

  return {
    data: result.data,
    error: result.error,
    loading: result.isLoading,
  };
};

export const useInfiniteSearchOptionsByPathQuery = ({
  pageSize,
  skip,
  variables,
}: InfiniteSearchOptionsByPathQueryOptions) => {
  const result = useInfiniteQuery<
    SearchOptionsByPathQuery,
    Error,
    InfiniteData<SearchOptionsByPathQuery, number>,
    ReturnType<typeof infiniteSearchOptionsByPathQueryKey>,
    number
  >({
    enabled: !skip,
    initialPageParam: 0,
    queryFn: ({ client, pageParam }) =>
      getGraphQLRequestFromClient(client)(SearchOptionsByPathDocument, {
        ...variables,
        filter: {
          ...(typeof variables.filter === 'object' && variables.filter !== null ? variables.filter : {}),
          limit: pageSize,
          offset: pageParam,
        },
      }),
    queryKey: infiniteSearchOptionsByPathQueryKey(variables, pageSize),
    getNextPageParam: (lastPage, _pages, lastPageParam) =>
      lastPage.searchOptionsByPath.has_next_page ? lastPageParam + pageSize : undefined,
    placeholderData: { pages: [], pageParams: [] },
    staleTime: ReactQueryStaleTime.MINUTES_15,
  });

  return {
    pages: result?.data?.pages ?? [],
    isFetching: result.isFetching,
    hasNextPage: result.hasNextPage,
    fetchNextPage: result.fetchNextPage,
  };
};
